import { IPromise, IScope } from "angular";
import { ICollection } from "@gtmhub/models";
import { IGoal } from "@gtmhub/goals/models";
import { IAssigneeIdMap } from "@gtmhub/assignees/models";
import { GoalService } from "@gtmhub/goals/goalService";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { IStateInit } from "@gtmhub/core/routing";
import { AssigneeActions, IAssigneesStoreState } from "@gtmhub/assignees";
import { INgRedux } from "@gtmhub/state-management";

interface IInspectGoalsWorkflowService {
  goalsStatName: string;
  planningSessionId: string;
  page: number;
}

export interface IInspectGoalsWorkflowCtrlScope extends IScope {
  indicators: {
    loading: boolean;
    paging: boolean;
  };
  params: IInspectGoalsWorkflowService;
  goals: ICollection<IGoal>;
  assigneesIdMap: IAssigneeIdMap;

  currentPage: number;
  totalPages: number;
  onPageChange(page: number): void;
}

const bindStateToScope = (state: IAssigneesStoreState) => ({
  assigneesIdMap: state.assignees.map,
});

const itemsPerPage = 10;

export class InspectGoalsWorkflowCtrl implements IStateInit {
  public static $inject = ["$scope", "$stateParams", "GoalService", "UIErrorHandlingService", "AssigneeActions", "$ngRedux"];

  constructor(
    private $scope: IInspectGoalsWorkflowCtrlScope,
    private $stateParams: IInspectGoalsWorkflowService,
    private goalService: GoalService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private assigneeActions: AssigneeActions,
    private $ngRedux: INgRedux
  ) {
    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);
  }

  public stateInit(): IPromise<unknown> {
    return this.init();
  }

  private strings = {
    loading: "loading",
    paging: "paging",
    approved: "approved",
    pending: "pending",
    draft: "draft",
    topLevelDashed: "top-level",
    crossSessionAlignedDashed: "cross-session-aligned",
    goals: "goals",
  };

  private getGoalsParams: { fields: string; approvalStatus: string; crossSessionAligned: boolean; parentId: string } = {
    fields: "id,name,ownerIds,sessionId,workflow{status,reviewersNeeded,reviewers}",
    approvalStatus: null,
    crossSessionAligned: null,
    parentId: null,
  };

  private init = (): IPromise<unknown> => {
    this.$scope.indicators = {
      loading: false,
      paging: false,
    };
    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());

    this.$scope.currentPage = 1;
    this.$scope.onPageChange = this.onPageChange;

    this.$scope.params = { ...this.$stateParams };

    this.getGoalsParams = this.configureGetGoalsParams(this.getGoalsParams, this.$stateParams.goalsStatName);

    this.$scope.indicators.loading = true;

    return this.goalService
      .getGoalsPaging(
        null,
        null,
        null,
        this.getGoalsParams.parentId,
        this.$stateParams.planningSessionId,
        null,
        null,
        null,
        0,
        itemsPerPage,
        null,
        null,
        null,
        null,
        this.getGoalsParams.fields,
        this.getGoalsParams.approvalStatus,
        this.getGoalsParams.crossSessionAligned
      )
      .then(
        (goals) => {
          this.$scope.indicators.loading = false;

          this.$scope.goals = goals;

          this.$scope.totalPages = Math.ceil(this.$scope.goals.totalCount / itemsPerPage);
        },
        (err) => this.handleIndicatorError(err, this.strings.loading)
      );
  };

  private configureGetGoalsParams = (
    params: { fields: string; approvalStatus: string; crossSessionAligned: boolean; parentId: string },
    statName: string
  ): { fields: string; approvalStatus: string; crossSessionAligned: boolean; parentId: string } => {
    switch (statName) {
      case this.strings.approved:
        params.approvalStatus = this.strings.approved;
        break;

      case this.strings.pending:
        params.approvalStatus = this.strings.pending;
        break;

      case this.strings.draft:
        params.approvalStatus = this.strings.draft;
        break;

      case this.strings.topLevelDashed:
        params.parentId = "";
        break;

      case this.strings.crossSessionAlignedDashed:
        params.crossSessionAligned = true;
        break;

      default:
        break;
    }

    return params;
  };

  private onPageChanged = (skip: number): void => {
    this.$scope.indicators.paging = true;

    this.goalService
      .getGoalsPaging(
        null,
        null,
        null,
        this.getGoalsParams.parentId,
        this.$stateParams.planningSessionId,
        null,
        null,
        null,
        skip,
        itemsPerPage,
        null,
        null,
        null,
        null,
        this.getGoalsParams.fields,
        this.getGoalsParams.approvalStatus,
        this.getGoalsParams.crossSessionAligned
      )
      .then(
        (goals) => {
          this.$scope.indicators.paging = false;

          this.$scope.goals = goals;
          this.$scope.totalPages = Math.ceil(this.$scope.goals.totalCount / itemsPerPage);
        },
        (err) => this.handleIndicatorError(err, this.strings.paging, true)
      );
  };

  private onPageChange = (page: number): void => {
    this.$scope.currentPage = page;

    const skip = (page - 1) * itemsPerPage;
    this.onPageChanged(skip);
  };

  private handleIndicatorError = <E>(err: E, indicator: string, inline = false): void => {
    this.uiErrorHandlingService.handleIndicator(err, { indicator, inline });
  };
}
